import { ArrayField, Field, JsonParseError, JsonParseErrorCode, Regexp } from "sparkson";
import { AnalyticsEndpoint } from "./AnalyticsEndpoint";
import { Part } from "./Part";
import { PlayerControls } from "./PlayerControls";
import { ScenarioElement } from "./ScenarioElement";
import { Skin } from "./Skin";

export class PlayerConfiguration {

  aspectRatio: number;

  constructor(
    @Field("c") public code: string,
    @ArrayField("se", ScenarioElement) public scenarioElements: ScenarioElement[],
    @ArrayField("ae", AnalyticsEndpoint) public analyticsEndpoints: AnalyticsEndpoint[],
    @Field("ar") aspectRatioStr: string,
    @ArrayField("pa", Part, true) public parts: Part[] = [],
    @Field("d", true) public videoDate: number,
    @Field("pc", true) public playerControls: PlayerControls = new PlayerControls(),
    @Field("cm") public campaign?: string,
    @Field("gr", true) public groupId?: number,
    @Field("sk", true) public skin?: Skin,
    @Field("am", true) public adminMode: boolean = false,
    @Field("ap", true) public autoplay: boolean = false,
    @Field("ms", true) public mutedStart: boolean = false,
    @Field("arbr", true) public autoRefreshBeforeReady: boolean = false,
    @Field("sa", true) public subtitlesActiveOnStart: boolean = true,
    @Field("st", true) @Regexp(/^(READY|PREPARING|DELETED)$/) public status: "READY" | "PREPARING" | "DELETED" = "READY",
    @Field("ju", true) public jsAssetsUrl?: string,
    @Field("piop", true) public playIconOnPoster: boolean = true,

  ) {
    this.parseAspectRatio(aspectRatioStr);
    this.validate();
  }

  private parseAspectRatio(aspectRatioStr: string) {
    const aspectRatioParts = aspectRatioStr.split("/");
    if (aspectRatioParts.length === 2) {
      this.aspectRatio = Number(aspectRatioParts[0]) / Number(aspectRatioParts[1]);
      if (isNaN(this.aspectRatio)) {
        throw new JsonParseError("Aspect ratio must be in the format 'width/height' (ie. '16/9')", JsonParseErrorCode.VALIDATION_FAILED);
      }
    } else {
      throw new JsonParseError("Aspect ratio must be in the format 'width/height' (ie. '16/9')", JsonParseErrorCode.VALIDATION_FAILED);
    }
  }

  private validate() {

    const initialScenarioElements = this.scenarioElements.filter((scenarioElement) => scenarioElement.initial);
    if (initialScenarioElements.length !== 1) {
      throw new JsonParseError("Configuration should have exactly one initial scenario element", JsonParseErrorCode.VALIDATION_FAILED);
    }

    const allScenarioElementIds = this.scenarioElements.map((scenarioElement) => scenarioElement.id);
    const uniqueScenarioElementIds = new Set();
    allScenarioElementIds.forEach((id) => uniqueScenarioElementIds.add(id));
    if (uniqueScenarioElementIds.size < allScenarioElementIds.length) {
      throw new JsonParseError("Configuration contains scenario elements with duplicated ids", JsonParseErrorCode.VALIDATION_FAILED);
    }

    if (this.scenarioElements.length > 1 && !this.parts.length) {
      throw new JsonParseError("Configuration with multiple scenario elements must have non empty parts array",
                               JsonParseErrorCode.VALIDATION_FAILED);
    }

    if (this.scenarioElements.length > 1) {
      if (!this.parts.length) {
        throw new JsonParseError("Scenario with multiple elements must have 'parts' array", JsonParseErrorCode.VALIDATION_FAILED);
      }
      for (const el of this.scenarioElements) {
        if (el.partId === undefined) {
          throw new JsonParseError("Each element in scenario with multiple elements must have 'partId'",
                                   JsonParseErrorCode.VALIDATION_FAILED);
        }
        if (!this.parts.find((part) => part.id === el.partId)) {
          throw new JsonParseError(`Part id '${el.partId}' for element with id '${el.id}' is not present in 'parts' array`,
                                   JsonParseErrorCode.VALIDATION_FAILED);
        }
      }
    }

    const totalPartsSize = this.parts.reduce((acc, part) => acc + part.size, 0);
    if (this.parts.length && totalPartsSize !== 100) {
      throw new JsonParseError("Total size of parts must be 100", JsonParseErrorCode.VALIDATION_FAILED);
    }

    if (this.parts.length) {
      const ctaOnlyScenarioElements = this.scenarioElements.filter((el) => !el.video && !el.animations.length);
      for (const el of ctaOnlyScenarioElements) {
        if (this.parts.find((part) => part.id === el.partId).size !== 0) {
          throw new JsonParseError("Part size for elements without video and animations must be 0", JsonParseErrorCode.VALIDATION_FAILED);
        }
      }
    }

    const allCTAIds = this.scenarioElements.reduce((acc, el) => acc.concat(el.ctas.map((cta) => cta.id)), []);
    const uniqueCTAIds = new Set();
    allCTAIds.forEach((id) => uniqueCTAIds.add(id));
    if (uniqueCTAIds.size < allCTAIds.length) {
      throw new JsonParseError("Configuration contains CTAs with duplicated ids", JsonParseErrorCode.VALIDATION_FAILED);
    }

    const hasVintomAnalyticsEndpoint = !!this.analyticsEndpoints.find((endpoint) => endpoint.provider === "vintom");
    if (hasVintomAnalyticsEndpoint && !this.videoDate) {
      throw new JsonParseError("Configuration with 'vintom' analytics endpoint must have 'videoDate'", JsonParseErrorCode.VALIDATION_FAILED);
    }
  }
}
